import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "notification-create-drawer__close-trigger" }
const _hoisted_2 = { class: "notification-create-drawer__layout" }
const _hoisted_3 = { class: "notification-create-drawer__recipients" }
const _hoisted_4 = {
  class: "notification-create-drawer__tabs",
  role: "tablist",
  "aria-label": "Тип получателей"
}
const _hoisted_5 = ["aria-selected"]
const _hoisted_6 = ["aria-selected"]
const _hoisted_7 = { class: "notification-create-drawer__content" }
const _hoisted_8 = { class: "notification-create-drawer__textarea-counter" }
const _hoisted_9 = { class: "notification-create-drawer__files" }
const _hoisted_10 = { class: "notification-create-drawer__footer" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import SearchInput from '../SearchInput.vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
  showStcWarningNotification,
} from '@/js/components/stc-notification';
import { submitManagerNotification } from './manager-notification-api';
import type { NotificationRecipientsData } from './types';

type RecipientTab = 'residents' | 'clusters';

type ResidentTableRow = {
  key: string;
  name: string;
};

type ClusterTableRow = {
  key: string;
  name: string;
};

const SUBJECT_MAX_LENGTH = 255;
const MESSAGE_MAX_LENGTH = 512;


export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationCreateDrawer',
  props: {
    open: { type: Boolean },
    recipients: {}
  },
  emits: ["update:open", "submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const loading = ref(false);
const recipientTab = ref<RecipientTab>('residents');
const recipientSearch = ref('');
const selectedResidentIds = ref<string[]>([]);
const selectedClusterIds = ref<string[]>([]);
const fileItems = ref<FileUploadItem[]>([]);
const fileUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const recipientsTableWrapperRef = ref<HTMLElement | null>(null);
const recipientsTableScrollY = ref<number>();

let recipientsTableResizeObserver: ResizeObserver | null = null;

const form = ref({
  subject: '',
  message: '',
});

const canSubmit = computed(
  () =>
    form.value.subject.trim() !== '' &&
    form.value.message.trim() !== '' &&
    (selectedResidentIds.value.length > 0 || selectedClusterIds.value.length > 0),
);

const hasChanges = computed(
  () =>
    form.value.subject.trim() !== '' ||
    form.value.message.trim() !== '' ||
    selectedResidentIds.value.length > 0 ||
    selectedClusterIds.value.length > 0 ||
    fileItems.value.length > 0,
);

const residentColumns: TableColumnsType<ResidentTableRow> = [
  {
    title: 'Резидент',
    dataIndex: 'name',
    key: 'name',
  },
];

const clusterColumns: TableColumnsType<ClusterTableRow> = [
  {
    title: 'Кластер',
    dataIndex: 'name',
    key: 'name',
  },
];

const recipientColumns = computed(() =>
  recipientTab.value === 'residents' ? residentColumns : clusterColumns,
);

const residentTableRows = computed<ResidentTableRow[]>(() => {
  const query = recipientSearch.value.trim().toLowerCase();

  return props.recipients.residents
    .filter((resident) => !query || resident.name.toLowerCase().includes(query))
    .map((resident) => ({
      key: resident.id,
      name: resident.name,
    }));
});

const clusterTableRows = computed<ClusterTableRow[]>(() => {
  const query = recipientSearch.value.trim().toLowerCase();

  return props.recipients.clusters
    .filter((cluster) => !query || cluster.name.toLowerCase().includes(query))
    .map((cluster) => ({
      key: cluster.id,
      name: cluster.name,
    }));
});

const recipientTableRows = computed(() =>
  recipientTab.value === 'residents' ? residentTableRows.value : clusterTableRows.value,
);

const rowSelection = computed(() => {
  if (recipientTab.value === 'residents') {
    const visibleIds = new Set(residentTableRows.value.map((row) => row.key));

    return {
      selectedRowKeys: selectedResidentIds.value.filter((id) => visibleIds.has(id)),
      onChange: (keys: (string | number)[]) => {
        const hiddenSelected = selectedResidentIds.value.filter((id) => !visibleIds.has(id));
        selectedResidentIds.value = [...hiddenSelected, ...keys.map(String)];
      },
    };
  }

  const selectedClusterKeys = clusterTableRows.value
    .filter((row) => selectedClusterIds.value.includes(row.key))
    .map((row) => row.key);

  return {
    selectedRowKeys: selectedClusterKeys,
    onChange: (keys: (string | number)[]) => {
      selectedClusterIds.value = keys.map(String);
    },
  };
});

function updateRecipientsTableScrollY(): void {
  const wrapper = recipientsTableWrapperRef.value;

  if (!wrapper) {
    return;
  }

  const tableHead = wrapper.querySelector('.ant-table-thead');
  const headHeight = tableHead?.getBoundingClientRect().height ?? 0;
  const nextScrollY = wrapper.clientHeight - headHeight;

  recipientsTableScrollY.value = nextScrollY > 0 ? nextScrollY : undefined;
}

function setupRecipientsTableResizeObserver(): void {
  teardownRecipientsTableResizeObserver();

  const wrapper = recipientsTableWrapperRef.value;

  if (!wrapper) {
    return;
  }

  if (typeof ResizeObserver === 'undefined') {
    updateRecipientsTableScrollY();
    return;
  }

  recipientsTableResizeObserver = new ResizeObserver(() => {
    updateRecipientsTableScrollY();
  });
  recipientsTableResizeObserver.observe(wrapper);
  updateRecipientsTableScrollY();
}

function teardownRecipientsTableResizeObserver(): void {
  recipientsTableResizeObserver?.disconnect();
  recipientsTableResizeObserver = null;
}

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) {
      teardownRecipientsTableResizeObserver();
      recipientsTableScrollY.value = undefined;
      resetFormState();
      return;
    }

    await nextTick();
    setupRecipientsTableResizeObserver();
  },
);

watch([recipientTab, recipientTableRows], async () => {
  if (!props.open) {
    return;
  }

  await nextTick();
  updateRecipientsTableScrollY();
});

onBeforeUnmount(() => {
  teardownRecipientsTableResizeObserver();
});

function resetFormState(): void {
  form.value = { subject: '', message: '' };
  selectedResidentIds.value = [];
  selectedClusterIds.value = [];
  recipientSearch.value = '';
  recipientTab.value = 'residents';
  fileUploadRef.value?.reset();
}

function handleReset(): void {
  resetFormState();
}

function handleClose(): void {
  emit('update:open', false);
}

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) {
    return;
  }

  const readyFiles = fileUploadRef.value?.getReadyFiles() ?? [];
  const hasUploadingFiles = fileItems.value.some((item) => item.status === 'uploading');

  if (hasUploadingFiles) {
    showStcWarningNotification('Дождитесь окончания загрузки файлов');
    return;
  }

  loading.value = true;

  try {
    await submitManagerNotification({
      subject: form.value.subject.trim(),
      message: form.value.message.trim(),
      residents: selectedResidentIds.value.length ? selectedResidentIds.value : undefined,
      clusters: selectedClusterIds.value.length ? selectedClusterIds.value : undefined,
      files: readyFiles,
    });

    showStcSuccessNotification('Уведомление отправлено');
    emit('update:open', false);
    emit('submit');
  } catch (error) {
    showStcErrorNotification(
      error instanceof Error ? error.message : 'Не удалось отправить уведомление',
    );
  } finally {
    loading.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createBlock(_component_a_drawer, {
    width: 1280,
    open: __props.open,
    class: "notification-create-drawer",
    "body-style": { padding: 0 },
    "footer-style": { textAlign: 'right', padding: 0 },
    "destroy-on-close": "",
    onClose: handleClose
  }, {
    title: _withCtx(() => [...(_cache[6] || (_cache[6] = [
      _createElementVNode("span", { class: "notification-create-drawer__title" }, "Новое уведомление", -1)
    ]))]),
    closeIcon: _withCtx(() => [
      _createElementVNode("span", _hoisted_1, [
        _createVNode(_unref(CloseOutlined))
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("button", {
          type: "button",
          class: "btn btn-secondary btn-medium",
          disabled: loading.value || !hasChanges.value,
          onClick: handleReset
        }, " Сбросить ", 8, _hoisted_11),
        _createElementVNode("button", {
          type: "button",
          class: "btn btn-primary btn-medium",
          disabled: loading.value || !canSubmit.value,
          onClick: handleSubmit
        }, _toDisplayString(loading.value ? 'Отправка...' : 'Применить'), 9, _hoisted_12)
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("section", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["btn btn-medium", recipientTab.value === 'residents' ? 'btn-black' : 'btn-outline']),
              role: "tab",
              "aria-selected": recipientTab.value === 'residents',
              onClick: _cache[0] || (_cache[0] = ($event: any) => (recipientTab.value = 'residents'))
            }, " Резиденты ", 10, _hoisted_5),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["btn btn-medium", recipientTab.value === 'clusters' ? 'btn-black' : 'btn-outline']),
              role: "tab",
              "aria-selected": recipientTab.value === 'clusters',
              onClick: _cache[1] || (_cache[1] = ($event: any) => (recipientTab.value = 'clusters'))
            }, " Кластеры ", 10, _hoisted_6)
          ]),
          _createVNode(SearchInput, {
            modelValue: recipientSearch.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((recipientSearch).value = $event)),
            class: "notification-create-drawer__search",
            placeholder: "Найти...",
            debounce: 0
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", {
            ref_key: "recipientsTableWrapperRef",
            ref: recipientsTableWrapperRef,
            class: "notification-create-drawer__recipients-table-wrap"
          }, [
            _createVNode(_component_a_table, {
              class: "notification-create-drawer__recipients-table",
              columns: recipientColumns.value,
              "data-source": recipientTableRows.value,
              "row-selection": rowSelection.value,
              pagination: false,
              bordered: true,
              scroll: recipientsTableScrollY.value ? { y: recipientsTableScrollY.value } : undefined,
              "row-key": "key",
              size: "middle"
            }, null, 8, ["columns", "data-source", "row-selection", "scroll"])
          ], 512)
        ]),
        _createVNode(_component_a_divider, {
          type: "vertical",
          class: "notification-create-drawer__divider"
        }),
        _createElementVNode("section", _hoisted_7, [
          _createVNode(_component_a_form, {
            layout: "vertical",
            class: "notification-create-drawer__form"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_form_item, {
                class: "stc-ant-field-skin",
                required: ""
              }, {
                label: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("span", { class: "notification-create-drawer__label" }, "Тема", -1)
                ]))]),
                default: _withCtx(() => [
                  _createVNode(_component_a_input, {
                    value: form.value.subject,
                    "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.value.subject) = $event)),
                    placeholder: "Тема уведомления",
                    maxlength: SUBJECT_MAX_LENGTH
                  }, null, 8, ["value"])
                ]),
                _: 1
              }),
              _createVNode(_component_a_form_item, {
                class: "stc-ant-field-skin notification-create-drawer__message-field",
                required: ""
              }, {
                label: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createElementVNode("span", { class: "notification-create-drawer__label" }, "Текст уведомления", -1)
                ]))]),
                default: _withCtx(() => [
                  _createVNode(_component_a_textarea, {
                    value: form.value.message,
                    "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.value.message) = $event)),
                    placeholder: "Текст",
                    maxlength: MESSAGE_MAX_LENGTH,
                    "auto-size": { minRows: 8, maxRows: 8 }
                  }, null, 8, ["value"]),
                  _createElementVNode("div", _hoisted_8, _toDisplayString(form.value.message.length) + " / " + _toDisplayString(MESSAGE_MAX_LENGTH), 1)
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(FormFileUpload, {
                  ref_key: "fileUploadRef",
                  ref: fileUploadRef,
                  modelValue: fileItems.value,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((fileItems).value = $event))
                }, null, 8, ["modelValue"])
              ])
            ]),
            _: 1
          })
        ])
      ])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})